import { useState, useEffect } from 'react'
import { XMarkIcon, ShieldCheckIcon } from '@heroicons/react/24/outline'
import api from '@/services/api'
import useAuthStore from '@/stores/authStore'

export default function RoleManagementModal({ 
  isOpen, 
  onClose, 
  user, 
  onRoleUpdated 
}) {
  const [newRole, setNewRole] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const { user: currentUser } = useAuthStore()

  useEffect(() => {
    if (isOpen && user) {
      setNewRole(user.role)
      setError('')
    }
  }, [isOpen, user])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!newRole || newRole === user.role) {
      setError('새로운 권한을 선택해주세요.')
      return
    }

    setLoading(true)
    setError('')

    try {
      await api.put('/users/role', {
        userId: user.id,
        role: newRole
      })

      // Trigger refresh of user data
      if (onRoleUpdated) {
        onRoleUpdated()
      }

      onClose()
    } catch (error) {
      console.error('Failed to update user role:', error)
      setError(error.response?.data?.message || '권한 변경에 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const getRoleLabel = (role) => {
    const labels = {
      'user': '일반 사용자',
      'admin': '관리자',
      'super_admin': '최고 관리자'
    }
    return labels[role] || role
  }

  const getRoleColor = (role) => {
    const colors = {
      'user': 'bg-blue-100 text-blue-800',
      'admin': 'bg-green-100 text-green-800',
      'super_admin': 'bg-purple-100 text-purple-800'
    }
    return colors[role] || 'bg-gray-100 text-gray-800'
  }

  const getRoleDescription = (role) => {
    const descriptions = {
      'user': '기본 사용자 권한으로 개인 계정 관리만 가능',
      'admin': '사용자 관리, 메모 작성 등 일반 관리 기능 접근 가능',
      'super_admin': '모든 시스템 기능 접근 가능 (출금, 스테이킹, 잔액 관리 등)'
    }
    return descriptions[role] || ''
  }

  // Only super_admin can change roles
  if (currentUser?.role !== 'super_admin') {
    return null
  }

  if (!isOpen || !user) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <ShieldCheckIcon className="h-5 w-5" />
            권한 변경 - {user.nickname || user.username}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2">현재 권한</h4>
          <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${getRoleColor(user.role)}`}>
            {getRoleLabel(user.role)}
          </span>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              새로운 권한 선택
            </label>
            <div className="space-y-3">
              {['user', 'admin', 'super_admin'].map((role) => (
                <label key={role} className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="role"
                    value={role}
                    checked={newRole === role}
                    onChange={(e) => setNewRole(e.target.value)}
                    className="mt-1"
                    disabled={user.id === currentUser?.id && role !== 'super_admin'}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(role)}`}>
                        {getRoleLabel(role)}
                      </span>
                      {user.id === currentUser?.id && role !== 'super_admin' && (
                        <span className="text-xs text-red-500">(변경 불가)</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      {getRoleDescription(role)}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {error && (
            <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">
              {error}
            </div>
          )}

          <div className="flex space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={loading || newRole === user.role}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? '변경중...' : '권한 변경'}
            </button>
          </div>
        </form>

        {user.id === currentUser?.id && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              ⚠️ 본인의 최고 관리자 권한은 변경할 수 없습니다.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}